import { REPORT_CONFIGURATION_NAME } from "../types/constants";
import { buildConfigurationForCopy } from "../utils/configurationUtils";
import { hasDuplicateOf } from "../utils/duplicationUtils";
import { getReportSourcePrefix } from "config";
import { useCallback, useRef, useState } from "react";
import {
  useCreateReportMutation,
  useUpdateReportMutation,
  useStoreReportConfigurationMutation,
  useLazyGetReportQuery,
  useLazyGetColumnsForReportQuery,
} from "services/reportsV3Service";

export const buildRequestSpec = (buildWorkingSpecification) => {
  const workingSpec = buildWorkingSpecification();
  const { order, ...restSpec } = workingSpec;
  return {
    ...restSpec,
    order: order.map(({ column, direction }) => ({ column, direction })),
  };
};

// Build the report entries contributed by a single `REPORT:` source table.
// A MULTI_TABLE_REPORT's reports list may only contain SINGLE_TABLE_REPORTs, so
// if the referenced report is itself multi-table we splice in its children
// rather than nesting it: its dependency children (already notForOutput) pass
// through verbatim, while its output child becomes the entry this source table
// points at. A single-table report yields one entry named by the source.
export const flattenReferencedReport = (source, report, columns) => {
  const expectedColumns = columns.map((col) => col.original);

  if (report.type === "MULTI_TABLE_REPORT") {
    const children = report.reports ?? [];
    return children.map((child) =>
      child.notForOutput
        ? child
        : {
            ...child,
            internalName: source,
            notForOutput: true,
            expectedColumns,
          }
    );
  }

  return [
    {
      displayName: report.displayName,
      description: report.description ?? "",
      internalName: source,
      type: report.type,
      specification: report.specification,
      notForOutput: true,
      expectedColumns,
      ...(hasDuplicateOf(report) ? { duplicateOf: report.duplicateOf } : {}),
    },
  ];
};

// Flatten the per-source entry lists and deduplicate by internalName, keeping
// the first occurrence so dependency ordering (dependencies before dependents)
// is preserved.
export const dedupeReferencedReports = (nestedReferenced) => {
  const seen = new Set();
  return nestedReferenced.flat().filter((entry) => {
    if (seen.has(entry.internalName)) return false;
    seen.add(entry.internalName);
    return true;
  });
};

export const useSaveReport = ({
  savedReport,
  buildReportData,
  buildConfigurationPayload,
  onSaveSuccess,
  onCreateSuccess,
  onSaveAsSuccess,
  onError,
  localReports = {},
}) => {
  const buildReportDataRef = useRef(buildReportData);
  const localReportsRef = useRef(localReports);
  localReportsRef.current = localReports;
  const buildConfigurationPayloadRef = useRef(buildConfigurationPayload);
  buildConfigurationPayloadRef.current = buildConfigurationPayload;
  buildReportDataRef.current = buildReportData;
  const onSaveSuccessRef = useRef(onSaveSuccess);
  onSaveSuccessRef.current = onSaveSuccess;
  const onCreateSuccessRef = useRef(onCreateSuccess);
  onCreateSuccessRef.current = onCreateSuccess;
  const onSaveAsSuccessRef = useRef(onSaveAsSuccess);
  onSaveAsSuccessRef.current = onSaveAsSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const [saveAsDialogOpen, setSaveAsDialogOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState("");
  const [saveAsDescription, setSaveAsDescription] = useState("");
  const [saveAsError, setSaveAsError] = useState("");

  const [
    createReport,
    { isLoading: isCreatingReport },
  ] = useCreateReportMutation();
  const [updateReport] = useUpdateReportMutation();
  const [storeConfiguration] = useStoreReportConfigurationMutation();
  const [fetchReport] = useLazyGetReportQuery();
  const [fetchColumnsForReport] = useLazyGetColumnsForReportQuery();
  const [isSavingReport, setIsSavingReport] = useState(false);

  const wrapReportIfNeeded = useCallback(
    async (reportData) => {
      const sourceTables = reportData.specification?.sourceTables ?? [];
      const reportSources = sourceTables.filter((t) => t.startsWith("REPORT:"));
      if (reportSources.length === 0) return reportData;

      // A source held only locally (a duplication) is embedded from memory,
      // preceded by whatever REPORT: sources its own specification names, so
      // a definition always appears before its first use.
      const resolveSource = async (source) => {
        const id = source.slice(getReportSourcePrefix().length);
        const local = localReportsRef.current[id];
        if (local) {
          const parentSources = (
            local.report.specification?.sourceTables ?? []
          ).filter((t) => t.startsWith(getReportSourcePrefix()));
          const parents = await Promise.all(parentSources.map(resolveSource));
          return [
            ...parents.flat(),
            ...flattenReferencedReport(source, local.report, local.columns),
          ];
        }
        const [report, columns] = await Promise.all([
          fetchReport(id).unwrap(),
          fetchColumnsForReport(id, true).unwrap(),
        ]);
        return flattenReferencedReport(source, report, columns);
      };

      const nestedReferenced = await Promise.all(
        reportSources.map(resolveSource)
      );

      const referencedReports = dedupeReferencedReports(nestedReferenced);

      return {
        displayName: reportData.displayName,
        description: reportData.description,
        type: "MULTI_TABLE_REPORT",
        reports: [...referencedReports, reportData],
      };
    },
    [fetchReport, fetchColumnsForReport]
  );

  // The payload is keyed by the saved report; a Save As stores it under the
  // new report's id instead.
  const saveConfiguration = useCallback(
    async (reportId) => {
      if (typeof buildConfigurationPayloadRef.current !== "function") return;
      const payload = buildConfigurationForCopy(
        savedReport,
        buildConfigurationPayloadRef.current(),
        savedReport?.internalName,
        reportId
      );
      if (Object.keys(payload).length > 0) {
        await storeConfiguration({
          reportId,
          configurationName: REPORT_CONFIGURATION_NAME,
          configuration: payload,
        }).unwrap();
      }
    },
    [savedReport, storeConfiguration]
  );

  const clearSaveAsDialog = useCallback(() => {
    setSaveAsName("");
    setSaveAsDescription("");
    setSaveAsError("");
  }, []);

  const clearSaveAsError = useCallback(() => {
    setSaveAsError("");
  }, []);

  const handleSaveAllChanges = useCallback(async () => {
    if (!savedReport?.internalName) return;
    setIsSavingReport(true);
    try {
      const reportData = await wrapReportIfNeeded(buildReportDataRef.current());
      await saveConfiguration(savedReport.internalName);
      await updateReport({
        reportId: savedReport.internalName,
        reportData,
      }).unwrap();
      onSaveSuccessRef.current?.();
    } catch (e) {
      onErrorRef.current?.(e);
    } finally {
      setIsSavingReport(false);
    }
  }, [savedReport, updateReport, saveConfiguration, wrapReportIfNeeded]);

  const handleCreate = useCallback(async () => {
    try {
      const reportData = await wrapReportIfNeeded(buildReportDataRef.current());
      const result = await createReport(reportData).unwrap();
      const newId = result?.id;
      if (newId) {
        await saveConfiguration(newId);
      }
      onCreateSuccessRef.current?.(newId);
    } catch (e) {
      onErrorRef.current?.(e);
    }
  }, [createReport, saveConfiguration, wrapReportIfNeeded]);

  const handleOpenSaveAsDialog = useCallback(() => {
    clearSaveAsDialog();
    setSaveAsDialogOpen(true);
  }, [clearSaveAsDialog]);

  const handleCloseSaveAsDialog = useCallback(() => {
    setSaveAsDialogOpen(false);
    clearSaveAsDialog();
  }, [clearSaveAsDialog]);

  const handleSaveAs = useCallback(async () => {
    if (!saveAsName.trim()) {
      setSaveAsError("Report name is required");
      return;
    }
    try {
      const baseData = buildReportDataRef.current();
      const reportSpec = await wrapReportIfNeeded({
        ...baseData,
        displayName: saveAsName.trim(),
        description: saveAsDescription.trim() || "",
      });
      const result = await createReport(reportSpec).unwrap();
      const newId = result?.id;
      if (newId) {
        await saveConfiguration(newId);
      }
      await onSaveAsSuccessRef.current?.(newId);
      handleCloseSaveAsDialog();
    } catch {
      setSaveAsError("Failed to save report. Please try again.");
    }
  }, [
    saveAsName,
    saveAsDescription,
    createReport,
    saveConfiguration,
    wrapReportIfNeeded,
    handleCloseSaveAsDialog,
  ]);

  return {
    saveAsDialogOpen,
    saveAsName,
    setSaveAsName,
    saveAsDescription,
    setSaveAsDescription,
    saveAsError,
    clearSaveAsError,
    isCreatingReport,
    isSavingReport,
    handleSaveAllChanges,
    handleCreate,
    handleOpenSaveAsDialog,
    handleCloseSaveAsDialog,
    handleSaveAs,
    wrapReportIfNeeded,
  };
};
export default useSaveReport;
